'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteEstablishment,
    getEstablishmentsByOrganization,
    Establishment,
    EstablishmentType,
    ESTABLISHMENT_TYPE_LABELS,
    toggleEstablishmentActive,
} from '@/lib/api/establishments/establishments-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { useHasLoadedOnce } from '@/lib/hooks/use-has-loaded-once'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdBusiness,
    MdClose,
    MdDelete,
    MdEdit,
    MdFilterList,
    MdSearch,
    MdToggleOff,
    MdToggleOn,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

type TypeTab = 'ALL' | EstablishmentType

const TYPE_TABS: { key: TypeTab; label: string }[] = [
    { key: 'ALL', label: 'Todos' },
    { key: 'CLINIC', label: 'Clínicas' },
    { key: 'HOSPITAL', label: 'Hospitais' },
    { key: 'GYM', label: 'Academias' },
    { key: 'LABORATORY', label: 'Laboratórios' },
    { key: 'PHARMACY', label: 'Farmácias' },
    { key: 'DENTAL_CLINIC', label: 'Odontológicas' },
    { key: 'VETERINARY', label: 'Veterinárias' },
    { key: 'OTHER', label: 'Outros' },
]

const TYPE_BADGE_CLASS: Record<EstablishmentType, string> = {
    CLINIC: 'badge-secondary',
    HOSPITAL: 'badge-error',
    GYM: 'badge-success',
    LABORATORY: 'badge-info',
    PHARMACY: 'badge-warning',
    REHABILITATION_CENTER: 'badge-accent',
    SPA: 'badge-secondary',
    DENTAL_CLINIC: 'badge-neutral',
    VETERINARY: 'badge-ghost',
    OTHER: 'badge-outline',
}

export default function EstablishmentsListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'establishments'
    )
    const [searchInput, setSearchInput] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [toggling, setToggling] = useState<number | null>(null)
    const [showFilters, setShowFilters] = useState(false)
    const [sortBy, setSortBy] = useState<string>('')
    const [statusFilter, setStatusFilter] = useState<
        'all' | 'active' | 'inactive'
    >('all')
    const [activeTab, setActiveTab] = useState<TypeTab>('ALL')

    const debouncedSearch = useDebounce(searchInput, 300)

    const sortFilters = useMemo(() => {
        if (sortBy === 'az') return { sortBy: 'name', sortOrder: 'asc' }
        if (sortBy === 'za') return { sortBy: 'name', sortOrder: 'desc' }
        if (sortBy === 'oldest')
            return { sortBy: 'createdAt', sortOrder: 'asc' }
        if (sortBy === 'newest')
            return { sortBy: 'createdAt', sortOrder: 'desc' }
        return {}
    }, [sortBy])

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            type: activeTab !== 'ALL' ? activeTab : undefined,
            active:
                statusFilter === 'all' ? undefined : statusFilter === 'active',
            ...sortFilters,
        }),
        [debouncedSearch, activeTab, statusFilter, sortFilters]
    )

    const activeFilterCount = [
        sortBy,
        statusFilter !== 'all' ? statusFilter : '',
    ].filter(Boolean).length

    const fetchFn = useCallback(
        (params: any) => {
            return getEstablishmentsByOrganization(
                user!.organizationId!,
                params
            )
        },
        [user?.organizationId]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<Establishment, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.organizationId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return
        setDeleting(true)
        try {
            const response = await deleteEstablishment(itemToDelete)
            if (response.success) {
                toast.success('Estabelecimento excluído com sucesso')
                reload()
                setShowDeleteModal(false)
                setItemToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir estabelecimento')
            }
        } catch {
            toast.error('Erro ao excluir estabelecimento')
        } finally {
            setDeleting(false)
        }
    }

    const handleToggleActive = async (id: number) => {
        setToggling(id)
        try {
            const response = await toggleEstablishmentActive(id)
            if (response.success) {
                toast.success(
                    response.result.active
                        ? 'Estabelecimento ativado'
                        : 'Estabelecimento desativado'
                )
                reload()
            } else {
                toast.error(response.error || 'Erro ao alterar status')
            }
        } catch {
            toast.error('Erro ao alterar status')
        } finally {
            setToggling(null)
        }
    }

    const hasLoadedOnce = useHasLoadedOnce(loading)

    // Só o PRIMEIRO carregamento troca a página pelo spinner; recarregar mantém
    // o campo de busca montado (ver useHasLoadedOnce).
    if (loading && !hasLoadedOnce) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando estabelecimentos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdBusiness className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Estabelecimentos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1
                                ? 'estabelecimento'
                                : 'estabelecimentos'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() =>
                            navigate('/registrations/establishments/new')
                        }
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            <div className="tabs tabs-boxed bg-base-100 border border-base-300/50 p-1 flex-wrap">
                {TYPE_TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`tab ${activeTab === tab.key ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, CNPJ ou cidade..."
                            className="input input-bordered w-full pl-10"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn btn-sm ${showFilters || activeFilterCount > 0 ? 'btn-secondary' : 'btn-ghost'} gap-1`}
                    >
                        <MdFilterList className="text-lg" />
                        <span className="hidden sm:inline">Filtros</span>
                    </button>
                    {searchInput && (
                        <button
                            type="button"
                            onClick={() => setSearchInput('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>

                {showFilters && (
                    <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-base-300/50">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-base-content/60">
                                Status
                            </label>
                            <select
                                className="select select-bordered select-sm w-40"
                                value={statusFilter}
                                onChange={e =>
                                    setStatusFilter(
                                        e.target.value as
                                            | 'all'
                                            | 'active'
                                            | 'inactive'
                                    )
                                }
                            >
                                <option value="all">Todos</option>
                                <option value="active">Apenas ativos</option>
                                <option value="inactive">
                                    Apenas inativos
                                </option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-base-content/60">
                                Ordenar por
                            </label>
                            <select
                                className="select select-bordered select-sm w-40"
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value)}
                            >
                                <option value="">Padrão</option>
                                <option value="az">A-Z</option>
                                <option value="za">Z-A</option>
                                <option value="newest">Mais novos</option>
                                <option value="oldest">Mais antigos</option>
                            </select>
                        </div>
                        {activeFilterCount > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSortBy('')
                                    setStatusFilter('all')
                                }}
                                className="btn btn-ghost btn-sm text-error"
                            >
                                Limpar filtros
                            </button>
                        )}
                    </div>
                )}
            </div>

            {loading && items.length === 0 ? (
                <div className="flex items-center justify-center min-h-[200px] gap-3 bg-base-100 rounded-xl border border-base-300/50">
                    <span className="loading loading-spinner loading-md text-secondary"></span>
                    <span className="text-sm text-base-content/60">
                        Buscando estabelecimentos...
                    </span>
                </div>
            ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBusiness className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchInput
                                ? 'Nenhum estabelecimento encontrado'
                                : 'Nenhum estabelecimento cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchInput
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando estabelecimentos'}
                        </p>
                        {!searchInput && canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/registrations/establishments/new'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro estabelecimento
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {items.map(item => (
                        <div
                            key={item.id}
                            className={`group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300 ${!item.active ? 'opacity-60' : ''}`}
                        >
                            <div className="flex items-center gap-4 p-4">
                                {item.logoUrl ? (
                                    <img
                                        src={item.logoUrl}
                                        alt={item.tradingName || item.name}
                                        className="w-12 h-12 rounded-lg object-cover border border-base-300/50"
                                    />
                                ) : (
                                    <div className="w-12 h-12 rounded-lg bg-base-200 flex items-center justify-center">
                                        <MdBusiness className="text-xl text-base-content/30" />
                                    </div>
                                )}

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {item.tradingName || item.name}
                                        </h3>
                                        <span
                                            className={`badge badge-sm ${TYPE_BADGE_CLASS[item.type]}`}
                                        >
                                            {
                                                ESTABLISHMENT_TYPE_LABELS[
                                                    item.type
                                                ]
                                            }
                                        </span>
                                        <span
                                            className={`badge badge-sm ${item.active ? 'badge-success' : 'badge-ghost'}`}
                                        >
                                            {item.active ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>

                                    {item.tradingName && (
                                        <p className="text-xs text-base-content/50 mb-1">
                                            {item.name}
                                        </p>
                                    )}

                                    <div className="flex flex-wrap items-center gap-3 text-sm">
                                        {item.city && item.state && (
                                            <span className="text-base-content/70">
                                                {item.city}/{item.state}
                                            </span>
                                        )}
                                        {item.phone1 && (
                                            <span className="text-base-content/70">
                                                {item.phone1}
                                            </span>
                                        )}
                                        {item.specialties.length > 0 && (
                                            <span className="badge badge-outline badge-sm">
                                                {item.specialties.length}{' '}
                                                especialidade
                                                {item.specialties.length !== 1
                                                    ? 's'
                                                    : ''}
                                            </span>
                                        )}
                                        {item.services.length > 0 && (
                                            <span className="badge badge-outline badge-sm">
                                                {item.services.length} serviço
                                                {item.services.length !== 1
                                                    ? 's'
                                                    : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    {canUpdate && (
                                        <button
                                            onClick={() =>
                                                handleToggleActive(item.id)
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title={
                                                item.active
                                                    ? 'Desativar'
                                                    : 'Ativar'
                                            }
                                            disabled={toggling === item.id}
                                        >
                                            {toggling === item.id ? (
                                                <span className="loading loading-spinner loading-xs"></span>
                                            ) : item.active ? (
                                                <MdToggleOn className="text-xl text-success" />
                                            ) : (
                                                <MdToggleOff className="text-xl text-base-content/50" />
                                            )}
                                        </button>
                                    )}
                                    {canUpdate && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/registrations/establishments/${item.id}`
                                                )
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() =>
                                                handleDelete(item.id)
                                            }
                                            className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="estabelecimento"
                        />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir estabelecimento"
                message="Tem certeza de que deseja excluir este estabelecimento? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
